import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, deleteDoc, getDoc, setDoc } from 'firebase/firestore';
import { firestore, storage } from '../firebase/firebaseConfig';
import { v4 as uuidv4 } from 'uuid';
import {
  DEFAULT_SHOWROOM_ADDRESS,
  DEFAULT_SHOWROOM_MAP_EMBED,
  SHOWROOM_ROOMS_COLLECTION,
  getStoragePathFromUrl,
  parseGoogleMapEmbed,
} from '../components/showroom/showroomSettings';

const VALID_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const MAX_SIZE = 10 * 1024 * 1024;
const GALLERY_COLUMNS = 3;
const REORDER_SPRING = { type: 'spring', stiffness: 320, damping: 30, mass: 0.7 };

function ShowroomRoomEditor({ room, onSaved, onDeleted }) {
  const [name, setName] = useState(room.name || '');
  const [published, setPublished] = useState(room.published !== false);
  const [address, setAddress] = useState('');
  const [mapInput, setMapInput] = useState('');
  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [isFileDragOver, setIsFileDragOver] = useState(false);
  const [draggingId, setDraggingId] = useState(null);
  const fileInputRef = useRef(null);
  const dragIndexRef = useRef(null);
  const galleryListRef = useRef(null);
  const gridMetricsRef = useRef(null);
  const imagesRef = useRef(images);
  imagesRef.current = images;

  const isFormValid = name.trim() !== '';
  const mapEmbedUrl = parseGoogleMapEmbed(mapInput);
  const isFirstRoom = room.order === 1;

  useEffect(() => {
    const fetchRoom = async () => {
      try {
        const snap = await getDoc(doc(firestore, SHOWROOM_ROOMS_COLLECTION, room.id));
        if (snap.exists()) {
          const data = snap.data();
          setName(typeof data.name === 'string' && data.name.trim() !== '' ? data.name : room.name);
          setPublished(data.published !== false);
          const savedAddress = typeof data.address === 'string' ? data.address : '';
          const savedMap = typeof data.mapEmbedUrl === 'string' ? data.mapEmbedUrl : '';
          setAddress(savedAddress || (isFirstRoom ? DEFAULT_SHOWROOM_ADDRESS : ''));
          setMapInput(savedMap || (isFirstRoom ? DEFAULT_SHOWROOM_MAP_EMBED : ''));
          setImages(
            Array.isArray(data.images)
              ? data.images
                  .filter((image) => image && image.url)
                  .map((image) => ({
                    id: image.id || uuidv4(),
                    url: image.url,
                  }))
              : []
          );
        } else {
          setName(room.name || '');
          setPublished(room.published !== false);
          setAddress(isFirstRoom ? DEFAULT_SHOWROOM_ADDRESS : '');
          setMapInput(isFirstRoom ? DEFAULT_SHOWROOM_MAP_EMBED : '');
        }
      } catch (error) {
        console.error('ショールーム設定の取得に失敗:', error);
        setFetchError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRoom();
  }, [room.id, room.name, room.published, isFirstRoom]);

  const addImageFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;

    const nextImages = [];
    files.forEach((file) => {
      if (!VALID_TYPES.includes(file.type)) {
        alert(`${file.name} はサポートされていないファイルタイプです。JPEG, PNG, GIF, WebPのみアップロードできます。`);
        return;
      }
      if (file.size > MAX_SIZE) {
        alert(`${file.name} のファイルサイズが大きすぎます。10MB以下のファイルをアップロードしてください。`);
        return;
      }
      nextImages.push({
        id: uuidv4(),
        file,
        previewUrl: URL.createObjectURL(file),
      });
    });

    if (nextImages.length > 0) {
      setImages((prev) => [...prev, ...nextImages]);
    }
  };

  const handleFilesSelected = (e) => {
    addImageFiles(e.target.files);
    e.target.value = '';
  };

  const isExternalFileDrag = (event) => {
    return Array.from(event.dataTransfer?.types || []).includes('Files');
  };

  const handleFileDragOver = (event) => {
    if (!isExternalFileDrag(event) || isSaving || isLoading) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    setIsFileDragOver(true);
  };

  const handleFileDragLeave = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsFileDragOver(false);
    }
  };

  const handleFileDrop = (event) => {
    if (!isExternalFileDrag(event)) return;
    event.preventDefault();
    event.stopPropagation();
    setIsFileDragOver(false);
    if (isSaving || isLoading) return;
    addImageFiles(event.dataTransfer.files);
  };

  const removeImage = (imageId) => {
    setImages((prev) => {
      const target = prev.find((image) => image.id === imageId);
      if (target && target.previewUrl) {
        URL.revokeObjectURL(target.previewUrl);
      }
      return prev.filter((image) => image.id !== imageId);
    });
  };

  const measureGalleryGrid = () => {
    const listEl = galleryListRef.current;
    const itemEl = listEl && listEl.querySelector('[data-gallery-item]');
    if (!listEl || !itemEl) return null;
    const listStyle = window.getComputedStyle(listEl);
    const itemRect = itemEl.getBoundingClientRect();
    return {
      padL: parseFloat(listStyle.paddingLeft) || 0,
      padT: parseFloat(listStyle.paddingTop) || 0,
      gap: parseFloat(listStyle.gap) || 0,
      cols: GALLERY_COLUMNS,
      cellW: itemRect.width,
      cellH: itemRect.height,
    };
  };

  const indexFromPoint = (clientX, clientY) => {
    const listEl = galleryListRef.current;
    const metrics = gridMetricsRef.current;
    const count = imagesRef.current.length;
    if (!listEl || !metrics || count === 0) return null;
    const listRect = listEl.getBoundingClientRect();
    const x = clientX - listRect.left - metrics.padL;
    const y = clientY - listRect.top - metrics.padT;
    if (x < -metrics.cellW / 2 || y < -metrics.cellH / 2) return null;
    const col = Math.max(0, Math.min(metrics.cols - 1, Math.floor(x / (metrics.cellW + metrics.gap))));
    const row = Math.max(0, Math.floor(y / (metrics.cellH + metrics.gap)));
    const index = Math.min(count - 1, row * metrics.cols + col);
    return index < 0 ? 0 : index;
  };

  const moveImageToIndex = (toIndex) => {
    setImages((prev) => {
      const fromIndex = dragIndexRef.current;
      if (fromIndex === null || toIndex === fromIndex) return prev;
      if (toIndex < 0 || toIndex >= prev.length || fromIndex < 0 || fromIndex >= prev.length) return prev;
      const next = Array.from(prev);
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      dragIndexRef.current = toIndex;
      return next;
    });
  };

  useEffect(() => {
    const onPointerMove = (event) => {
      if (dragIndexRef.current === null || isSaving) return;
      const nextIndex = indexFromPoint(event.clientX, event.clientY);
      if (nextIndex === null) return;
      moveImageToIndex(nextIndex);
    };
    const endReorder = () => {
      if (dragIndexRef.current === null) return;
      dragIndexRef.current = null;
      gridMetricsRef.current = null;
      setDraggingId(null);
    };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', endReorder);
    window.addEventListener('pointercancel', endReorder);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', endReorder);
      window.removeEventListener('pointercancel', endReorder);
    };
  }, [isSaving]);

  const handleReorderPointerDown = (index) => (event) => {
    if (isSaving || event.button !== 0) return;
    event.preventDefault();
    dragIndexRef.current = index;
    gridMetricsRef.current = measureGalleryGrid();
    setDraggingId(images[index] && images[index].id);
  };

  const deleteUnusedImages = async (previousUrls, nextUrls) => {
    const remaining = new Set(nextUrls);
    const removed = previousUrls.filter((url) => url && !remaining.has(url));
    await Promise.all(
      removed.map(async (url) => {
        const path = getStoragePathFromUrl(url);
        if (!path) return;
        try {
          await deleteObject(ref(storage, path));
        } catch (error) {
          console.error('ギャラリー画像の削除に失敗:', error);
        }
      })
    );
  };

  const saveRoom = async () => {
    if (!isFormValid || isSaving) return;

    try {
      setIsSaving(true);
      const snap = await getDoc(doc(firestore, SHOWROOM_ROOMS_COLLECTION, room.id));
      const previousUrls = snap.exists() && Array.isArray(snap.data().images)
        ? snap.data().images.map((image) => image.url).filter(Boolean)
        : [];

      const uploadedImages = [];
      for (const image of images) {
        if (image.file) {
          const fileExtension = image.file.name.split('.').pop();
          const storagePath = `showroomRooms/${room.id}/${image.id}.${fileExtension}`;
          const storageRef = ref(storage, storagePath);
          await uploadBytes(storageRef, image.file);
          const url = await getDownloadURL(storageRef);
          uploadedImages.push({ id: image.id, url });
        } else if (image.url) {
          uploadedImages.push({ id: image.id, url: image.url });
        }
      }

      const savedName = name.trim();
      const savedAddress = address.replace(/\r\n/g, '\n').trim();
      const savedMapEmbedUrl = mapEmbedUrl;

      await setDoc(
        doc(firestore, SHOWROOM_ROOMS_COLLECTION, room.id),
        {
          name: savedName,
          address: savedAddress,
          mapEmbedUrl: savedMapEmbedUrl,
          images: uploadedImages,
          published,
          updatedAt: new Date(),
        },
        { merge: true }
      );

      await deleteUnusedImages(previousUrls, uploadedImages.map((image) => image.url));

      images.forEach((image) => {
        if (image.previewUrl) URL.revokeObjectURL(image.previewUrl);
      });
      setImages(uploadedImages);
      setMapInput(savedMapEmbedUrl);
      setShowNotification(true);
      if (onSaved) {
        onSaved({
          id: room.id,
          name: savedName,
          address: savedAddress,
          mapEmbedUrl: savedMapEmbedUrl,
          images: uploadedImages,
          published,
          order: room.order,
        });
      }
    } catch (error) {
      console.error('ショールーム設定の保存に失敗:', error);
      alert(`エラー: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const deleteRoom = async () => {
    if (isDeleting || isSaving) return;

    let deleted = false;
    try {
      setIsDeleting(true);
      const roomRef = doc(firestore, SHOWROOM_ROOMS_COLLECTION, room.id);
      const snap = await getDoc(roomRef);
      const storedUrls = snap.exists() && Array.isArray(snap.data().images)
        ? snap.data().images.map((image) => image.url).filter(Boolean)
        : [];

      await deleteUnusedImages(storedUrls, []);
      await deleteDoc(roomRef);

      images.forEach((image) => {
        if (image.previewUrl) URL.revokeObjectURL(image.previewUrl);
      });
      deleted = true;
    } catch (error) {
      console.error('ショールームの削除に失敗:', error);
      alert(`エラー: ${error.message}`);
    } finally {
      if (!deleted) setIsDeleting(false);
    }

    if (deleted && onDeleted) onDeleted(room.id);
  };

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '20px',
      width: '100%',
    },
    form: {
      width: '100%',
      maxWidth: '800px',
      backgroundColor: 'white',
      padding: '20px',
      borderRadius: '8px',
      boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',
      textAlign: 'left',
      position: 'relative',
    },
    sectionTitle: {
      display: 'block',
      marginBottom: '8px',
      fontWeight: 'bold',
    },
    inputGroup: {
      marginBottom: '20px',
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      fontWeight: 'bold',
    },
    statusRow: {
      display: 'flex',
      gap: '8px',
    },
    statusButton: {
      padding: '8px 16px',
      borderRadius: '4px',
      border: '1px solid #ccc',
      backgroundColor: 'white',
      color: '#333',
      cursor: isSaving || isLoading || isDeleting ? 'not-allowed' : 'pointer',
    },
    statusButtonActive: {
      backgroundColor: '#110b41ff',
      color: 'white',
      border: '1px solid #110b41ff',
    },
    inputField: {
      width: '100%',
      padding: '8px',
      borderRadius: '4px',
      border: '1px solid #ccc',
      boxSizing: 'border-box',
    },
    textarea: {
      width: '100%',
      minHeight: '160px',
      padding: '8px',
      borderRadius: '4px',
      border: '1px solid #ccc',
      boxSizing: 'border-box',
      lineHeight: '1.8',
      fontFamily: 'inherit',
      fontSize: '14px',
      resize: 'vertical',
    },
    mapTextarea: {
      width: '100%',
      minHeight: '100px',
      padding: '8px',
      borderRadius: '4px',
      border: '1px solid #ccc',
      boxSizing: 'border-box',
      lineHeight: '1.6',
      fontFamily: 'inherit',
      fontSize: '13px',
      resize: 'vertical',
    },
    hint: {
      marginTop: '8px',
      color: '#666',
      fontSize: '12px',
    },
    dropZone: {
      width: '100%',
      boxSizing: 'border-box',
      padding: '24px 16px',
      marginBottom: '12px',
      border: isFileDragOver ? '2px dashed #110b41ff' : '2px dashed #ccc',
      borderRadius: '8px',
      backgroundColor: isFileDragOver ? '#eef2ff' : '#fafafa',
      textAlign: 'center',
      cursor: isSaving || isLoading ? 'not-allowed' : 'pointer',
    },
    dropZoneText: {
      margin: 0,
      color: '#333',
      fontSize: '14px',
    },
    dropZoneSubText: {
      margin: '8px 0 0',
      color: '#666',
      fontSize: '12px',
    },
    hiddenFileInput: {
      display: 'none',
    },
    galleryList: {
      display: 'grid',
      gridTemplateColumns: `repeat(${GALLERY_COLUMNS}, 1fr)`,
      gap: '10px',
      minHeight: '120px',
      padding: '8px',
      border: '1px dashed #ccc',
      borderRadius: '4px',
      backgroundColor: '#fafafa',
      overflow: 'visible',
    },
    galleryItem: {
      width: '100%',
      position: 'relative',
      borderRadius: '4px',
    },
    galleryImageWrap: {
      width: '100%',
      aspectRatio: '1 / 1',
      overflow: 'hidden',
      userSelect: 'none',
    },
    galleryImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      borderRadius: '4px',
      display: 'block',
      border: '1px solid #ddd',
      userSelect: 'none',
      WebkitUserDrag: 'none',
    },
    emptyGallery: {
      gridColumn: '1 / -1',
      color: '#666',
      fontSize: '14px',
      margin: '12px',
    },
    dragHint: {
      fontSize: '11px',
      color: '#666',
      textAlign: 'center',
      marginTop: '4px',
    },
    deleteButton: {
      position: 'absolute',
      top: '4px',
      right: '4px',
      width: '24px',
      height: '24px',
      border: 'none',
      borderRadius: '50%',
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      color: 'white',
      cursor: 'pointer',
      lineHeight: '24px',
      padding: 0,
    },
    mapPreview: {
      marginTop: '12px',
      border: 0,
      width: '100%',
      height: '240px',
    },
    buttonRow: {
      display: 'flex',
      gap: '12px',
      flexWrap: 'wrap',
    },
    submitButton: {
      padding: '10px 20px',
      backgroundColor: isFormValid && !isSaving && !isDeleting ? '#110b41ff' : '#ccc',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: isFormValid && !isSaving && !isDeleting ? 'pointer' : 'not-allowed',
      flex: '1 1 240px',
    },
    deleteRoomButton: {
      padding: '10px 20px',
      backgroundColor: 'white',
      color: isSaving || isLoading || isDeleting ? '#ccc' : '#c0392b',
      border: `1px solid ${isSaving || isLoading || isDeleting ? '#ccc' : '#c0392b'}`,
      borderRadius: '4px',
      cursor: isSaving || isLoading || isDeleting ? 'not-allowed' : 'pointer',
      flex: '0 1 auto',
    },
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '20px',
      boxSizing: 'border-box',
    },
    modalContent: {
      backgroundColor: 'white',
      padding: '24px',
      borderRadius: '8px',
      maxWidth: '480px',
      width: '100%',
      boxShadow: '0 0 10px rgba(0, 0, 0, 0.3)',
      textAlign: 'center',
    },
    modalText: {
      margin: '0 0 8px',
      lineHeight: '1.7',
    },
    modalActions: {
      display: 'flex',
      justifyContent: 'center',
      gap: '12px',
      marginTop: '20px',
    },
    cancelButton: {
      padding: '10px 20px',
      backgroundColor: 'white',
      color: '#110b41ff',
      border: '1px solid #110b41ff',
      borderRadius: '4px',
      cursor: isDeleting ? 'not-allowed' : 'pointer',
    },
    confirmDeleteButton: {
      padding: '10px 20px',
      backgroundColor: isDeleting ? '#ccc' : '#c0392b',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: isDeleting ? 'not-allowed' : 'pointer',
      minWidth: '88px',
    },
    loadingOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(255, 255, 255, 0.7)',
      display: isSaving ? 'flex' : 'none',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: '8px',
      zIndex: 10,
    },
    loadingText: {
      fontSize: '16px',
      fontWeight: 'bold',
    },
    notification: {
      backgroundColor: 'white',
      padding: '20px',
      borderRadius: '8px',
      textAlign: 'center',
      boxShadow: '0 0 10px rgba(0, 0, 0, 0.3)',
      marginTop: '20px',
    },
    okButton: {
      padding: '10px',
      backgroundColor: '#110b41ff',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      marginTop: '20px',
    },
    error: {
      color: 'red',
      marginBottom: '20px',
    },
    emptyText: {
      color: '#666',
      fontSize: '14px',
      marginBottom: '20px',
    },
  };

  return (
    <div style={styles.container}>
      <form style={styles.form} onSubmit={(e) => e.preventDefault()}>
        <div style={styles.loadingOverlay}>
          <span style={styles.loadingText}>保存中...</span>
        </div>

        <div style={styles.sectionTitle}>
          <p>{room.name} の設定</p>
        </div>

        {isLoading && <p style={styles.emptyText}>読み込み中...</p>}
        {fetchError && <p style={styles.error}>エラー: {fetchError}</p>}

        <div style={styles.inputGroup}>
          <span style={styles.label}>公開状態:</span>
          <div style={styles.statusRow}>
            <button
              type="button"
              onClick={() => setPublished(true)}
              style={{
                ...styles.statusButton,
                ...(published ? styles.statusButtonActive : {}),
              }}
              disabled={isSaving || isLoading || isDeleting}
            >
              公開
            </button>
            <button
              type="button"
              onClick={() => setPublished(false)}
              style={{
                ...styles.statusButton,
                ...(!published ? styles.statusButtonActive : {}),
              }}
              disabled={isSaving || isLoading || isDeleting}
            >
              非公開
            </button>
          </div>
          <p style={styles.hint}>「このショールームを登録」で公開状態も保存されます。非公開のショールームは公開ページに表示されません。</p>
        </div>

        <div style={styles.inputGroup}>
          <label style={styles.label} htmlFor={`showroom-name-${room.id}`}>ショールーム名:</label>
          <input
            id={`showroom-name-${room.id}`}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={styles.inputField}
            disabled={isSaving || isLoading}
          />
        </div>

        <div
          style={styles.inputGroup}
          onDragOver={handleFileDragOver}
          onDragLeave={handleFileDragLeave}
          onDrop={handleFileDrop}
        >
          <label style={styles.label} htmlFor={`showroom-images-${room.id}`}>ショールーム画像:</label>
          <input
            id={`showroom-images-${room.id}`}
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            multiple
            onChange={handleFilesSelected}
            style={styles.hiddenFileInput}
            disabled={isSaving || isLoading}
          />
          <div
            style={styles.dropZone}
            onClick={() => {
              if (isSaving || isLoading) return;
              fileInputRef.current?.click();
            }}
          >
            <p style={styles.dropZoneText}>
              {isFileDragOver ? 'ここに画像をドロップ' : '画像をドラッグ＆ドロップ'}
            </p>
            <p style={styles.dropZoneSubText}>複数枚まとめて追加できます。クリックして選択することもできます。</p>
          </div>
          <p style={styles.hint}>追加した画像はドラッグで表示順を変更できます。最後に「このショールームを登録」で保存します。</p>
          <div ref={galleryListRef} style={styles.galleryList}>
            {images.length === 0 && (
              <p style={styles.emptyGallery}>画像はまだありません。</p>
            )}
            {images.map((image, index) => {
              const isDragging = draggingId === image.id;
              return (
                <motion.div
                  key={image.id}
                  data-gallery-item="true"
                  layout
                  initial={false}
                  transition={REORDER_SPRING}
                  animate={{
                    scale: isDragging ? 1.04 : 1,
                    zIndex: isDragging ? 4 : 1,
                    boxShadow: isDragging
                      ? '0 14px 28px rgba(17, 11, 65, 0.22)'
                      : '0 0 0 rgba(17, 11, 65, 0)',
                  }}
                  style={styles.galleryItem}
                >
                  <div
                    onPointerDown={handleReorderPointerDown(index)}
                    style={{
                      ...styles.galleryImageWrap,
                      cursor: isSaving ? 'not-allowed' : (draggingId ? 'grabbing' : 'grab'),
                      touchAction: 'none',
                      borderRadius: '4px',
                    }}
                  >
                    <img
                      src={image.previewUrl || image.url}
                      alt={`ショールーム画像 ${index + 1}`}
                      draggable={false}
                      onDragStart={(event) => event.preventDefault()}
                      style={styles.galleryImage}
                    />
                  </div>
                  <button
                    type="button"
                    style={styles.deleteButton}
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={() => removeImage(image.id)}
                    disabled={isSaving}
                  >
                    ×
                  </button>
                  <div style={styles.dragHint}>{index + 1}</div>
                </motion.div>
              );
            })}
          </div>
        </div>

        <div style={styles.inputGroup}>
          <label style={styles.label} htmlFor={`showroom-address-${room.id}`}>住所:</label>
          <textarea
            id={`showroom-address-${room.id}`}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            style={styles.textarea}
            disabled={isSaving || isLoading}
          />
        </div>

        <div style={styles.inputGroup}>
          <label style={styles.label} htmlFor={`showroom-map-${room.id}`}>アクセスマップ:</label>
          <textarea
            id={`showroom-map-${room.id}`}
            value={mapInput}
            onChange={(e) => setMapInput(e.target.value)}
            style={styles.mapTextarea}
            placeholder='Google マップの「共有」→「地図を埋め込む」の iframe、または embed URL を貼り付けてください'
            disabled={isSaving || isLoading}
          />
          <p style={styles.hint}>iframe の HTML をそのまま貼っても、src の地図 URL だけを抜き出して使います。</p>
          {mapEmbedUrl.includes('google.com/maps/embed') && (
            <iframe
              title={`${name || room.name} アクセスマッププレビュー`}
              src={mapEmbedUrl}
              style={styles.mapPreview}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          )}
        </div>

        <div style={styles.buttonRow}>
          <button
            type="button"
            onClick={saveRoom}
            style={styles.submitButton}
            disabled={!isFormValid || isSaving || isLoading || isDeleting}
          >
            {isSaving ? '保存中...' : isFormValid ? 'このショールームを登録' : 'ショールーム名を入力してください'}
          </button>
          <button
            type="button"
            onClick={() => setShowDeleteConfirm(true)}
            style={styles.deleteRoomButton}
            disabled={isSaving || isLoading || isDeleting}
          >
            このショールームを削除
          </button>
        </div>
      </form>

      {showDeleteConfirm && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <p style={styles.modalText}>
              「{name.trim() || room.name}」を削除します。
            </p>
            <p style={styles.modalText}>
              削除すると元に戻すことはできません。よろしいですか？
            </p>
            <div style={styles.modalActions}>
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                style={styles.cancelButton}
                disabled={isDeleting}
              >
                キャンセル
              </button>
              <button
                type="button"
                onClick={deleteRoom}
                style={styles.confirmDeleteButton}
                disabled={isDeleting}
              >
                {isDeleting ? '削除中...' : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showNotification && (
        <div style={styles.notification}>
          <p>ショールームを登録しました。</p>
          <button onClick={() => setShowNotification(false)} style={styles.okButton}>OK</button>
        </div>
      )}
    </div>
  );
}

export default ShowroomRoomEditor;
